import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "../components/Header";
import { api, API, formatApiError, formatDateTime } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../i18n";
import { Send, ArrowLeft, MessageCircle, Paperclip, FileText, X, Flag, AlertTriangle } from "lucide-react";

const PRIORITY_META = {
  normal: { key: "msg.normal", pill: "bg-[#E2E8F0] text-[#334155] border-[#CBD5E1]", badge: "bg-[#E2E8F0] text-[#334155]", border: "", bubble: "", Icon: null },
  high: { key: "msg.high", pill: "bg-[#F59E0B] text-white border-[#F59E0B]", badge: "bg-[#FDE68A] text-[#92400E]", border: "border-l-[6px] border-l-[#F59E0B]", bubble: "bg-[#FFFBEB]", Icon: Flag },
  urgent: { key: "msg.urgent", pill: "bg-[#DC2626] text-white border-[#DC2626]", badge: "bg-[#FECACA] text-[#991B1B]", border: "border-l-[6px] border-l-[#DC2626]", bubble: "bg-[#FEF2F2]", Icon: AlertTriangle },
};

export default function MessagesPage() {
  const { user } = useAuth();
  const { t } = useT();
  const { companyId: paramCompanyId } = useParams();
  const companyId = paramCompanyId || user?.company_id;
  const isAccountant = user?.role === "accountant";

  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [priority, setPriority] = useState("normal");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);
  const endRef = useRef(null);

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      const { data } = await api.get("/messages", { params: { company_id: companyId } });
      setMessages(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  }, [companyId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000); // simple polling
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (isAccountant && companyId) {
      api.get("/companies").then(({ data }) => {
        const c = data.find((x) => x.id === companyId);
        if (c) setCompanyName(c.name);
      });
    } else if (user?.company) {
      setCompanyName(user.company);
    }
  }, [companyId, isAccountant, user?.company]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const send = async (e) => {
    e.preventDefault();
    if ((!body.trim() && !file) || !companyId) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("company_id", companyId);
      fd.append("body", body);
      fd.append("priority", priority);
      if (file) fd.append("file", file);
      const { data } = await api.post("/messages", fd);
      setMessages((m) => [...m, data]);
      setBody("");
      setPriority("normal");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSending(false); }
  };

  const toggleResolved = async (m) => {
    try {
      const { data } = await api.patch(`/messages/${m.id}`, { resolved: !m.resolved });
      setMessages((prev) => prev.map((x) => (x.id === m.id ? data : x)));
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const backHref = isAccountant ? "/contabilista/definicoes?tab=empresas" : "/cliente";

  if (!companyId) {
    return (
      <div className="paper-grain min-h-screen">
        <Header />
        <div className="max-w-3xl mx-auto p-6">
          <p className="text-[color:var(--ink-muted)]">{t("msg.no_company")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="paper-grain min-h-screen flex flex-col">
      <Header />
      <main className="max-w-3xl w-full mx-auto px-4 sm:px-6 py-6 flex-1 flex flex-col" data-testid="messages-page">
        <Link to={backHref} className="inline-flex items-center gap-2 text-sm text-[color:var(--ink-muted)] hover:text-[color:var(--ink)]">
          <ArrowLeft size={16} /> {t("common.back")}
        </Link>
        <div className="mt-3 flex items-center gap-2">
          <MessageCircle className="text-[color:var(--primary)]" size={20} />
          <span className="label-eyebrow">{t("msg.eyebrow")}</span>
        </div>
        <h1 className="font-display text-2xl sm:text-3xl mt-1" data-testid="messages-title">
          {isAccountant ? companyName || t("role.client") : t("msg.team")}
        </h1>

        <div className="card-soft mt-5 flex-1 flex flex-col overflow-hidden p-0">
          <div className="flex-1 overflow-y-auto p-5 space-y-3 min-h-[300px] max-h-[60vh]">
            {messages.length === 0 ? (
              <p className="text-center text-[color:var(--ink-muted)] py-8" data-testid="messages-empty">
                {t("msg.empty")}
              </p>
            ) : messages.map((m) => {
              const mine = m.sender_id === user?.id;
              const pmeta = PRIORITY_META[m.priority] || PRIORITY_META.normal;
              const highlighted = m.priority && m.priority !== "normal";
              const darkBubble = mine && !highlighted;
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`message-${m.id}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${pmeta.border} ${
                    highlighted ? `${pmeta.bubble} text-[color:var(--ink)] border border-[color:var(--border)]`
                    : mine ? "bg-[color:var(--primary)] text-white" : "bg-[color:var(--paper)] border border-[color:var(--border)]"
                  }`}>
                    {highlighted && (
                      <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full mb-1.5 ${pmeta.badge}`} data-testid={`priority-badge-${m.id}`}>
                        <pmeta.Icon size={11} /> {t(pmeta.key)}
                      </span>
                    )}
                    <div className={`text-xs mb-1 ${darkBubble ? "text-white/70" : "text-[color:var(--ink-muted)]"}`}>
                      {m.sender_name} · {m.sender_role === "accountant" ? "Perfinco" : t("msg.sender_client")} · {formatDateTime(m.created_at)}
                    </div>
                    {m.body && <div className="whitespace-pre-wrap text-sm leading-relaxed">{m.body}</div>}
                    {m.attachment && (
                      <a
                        href={`${API}/messages/${m.id}/attachment`}
                        target="_blank"
                        rel="noreferrer"
                        className={`mt-2 block ${m.body ? "pt-2 border-t " + (darkBubble ? "border-white/20" : "border-[color:var(--border)]") : ""}`}
                        data-testid={`attachment-${m.id}`}
                      >
                        {m.attachment.content_type?.startsWith("image/") ? (
                          <img
                            src={`${API}/messages/${m.id}/attachment`}
                            alt={m.attachment.filename}
                            className="rounded-lg max-h-48 w-auto object-contain bg-black/5"
                          />
                        ) : (
                          <span className={`inline-flex items-center gap-2 text-sm underline-offset-2 hover:underline ${darkBubble ? "text-white" : "text-[color:var(--primary)]"}`}>
                            <FileText size={16} /> {m.attachment.filename}
                          </span>
                        )}
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
            <div ref={endRef} />
          </div>
          <form onSubmit={send} className="border-t border-[color:var(--border)] p-4">
            {file && (
              <div className="flex items-center gap-2 text-xs bg-[color:var(--paper)] border border-[color:var(--border)] rounded-lg px-3 py-2 w-fit max-w-full mb-2" data-testid="selected-file-chip">
                <FileText size={14} className="text-[color:var(--primary)] shrink-0" />
                <span className="truncate">{file.name}</span>
                <button type="button" onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }} className="p-0.5 rounded hover:bg-[color:var(--border)]" data-testid="remove-file-btn">
                  <X size={14} />
                </button>
              </div>
            )}
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[11px] uppercase tracking-wide text-[color:var(--ink-muted)]">{t("msg.priority")}</span>
              {["normal", "high", "urgent"].map((v) => {
                const meta = PRIORITY_META[v];
                const active = priority === v;
                return (
                  <button
                    type="button"
                    key={v}
                    onClick={() => setPriority(v)}
                    className={`text-xs px-2.5 py-0.5 rounded-full border inline-flex items-center gap-1 transition-colors ${active ? meta.pill : "border-[color:var(--border)] text-[color:var(--ink-muted)] hover:border-[color:var(--ink-muted)]"}`}
                    data-testid={`priority-${v}`}
                  >
                    {meta.Icon && <meta.Icon size={12} />}
                    {t(meta.key)}
                  </button>
                );
              })}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="hidden"
              data-testid="message-file-input"
            />
            <div className="flex items-center gap-1 rounded-full border border-[color:var(--border)] bg-[color:var(--paper)] pl-1.5 pr-1.5 py-1 focus-within:border-[color:var(--primary)] transition-colors">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="p-2 rounded-full text-[color:var(--ink-muted)] hover:text-[color:var(--primary)] transition-colors shrink-0"
                data-testid="attach-file-btn"
                title={t("msg.attach")}
              >
                <Paperclip size={18} />
              </button>
              <input
                className="flex-1 bg-transparent outline-none text-sm py-1.5 min-w-0"
                placeholder={t("msg.placeholder")}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                data-testid="message-input"
              />
              <button
                type="submit"
                disabled={sending || (!body.trim() && !file)}
                className="shrink-0 w-9 h-9 rounded-full bg-[color:var(--primary)] text-white inline-flex items-center justify-center disabled:opacity-40 hover:opacity-90 transition-opacity"
                data-testid="send-message-btn"
                title={t("msg.send")}
              >
                <Send size={16} />
              </button>
            </div>
          </form>
          {error && <div className="px-4 pb-3 text-xs text-[#991B1B]">{error}</div>}
        </div>
      </main>
    </div>
  );
}
